/**
 * People WRITE endpoints — request schemas (Pattern B+,
 * `apps/api/src/routes/people/people.ts`).
 *
 * `createPersonSchema` / `updatePersonSchema` ARE THE FIELD WHITELIST for the
 * `create_person(p_organization_id, p_payload)` and
 * `update_person(p_person_id, p_payload)` INVOKER RPCs
 * (`20260924072530_people_backend_writes.sql`). The route hands the parsed
 * body to the RPC as `p_payload` UNCHANGED, so the keys below are exactly the
 * payload keys the RPC reads (`PERSON_SCALAR_KEYS` + `PERSON_CHILD_KEYS`, and
 * `PERSON_PHOTO_KEYS` on update only). Both top-level objects are `.strict()`:
 * zod silently STRIPS unknown keys otherwise, so a body carrying
 * `organization_id` or `auth_user_id` would 200 with the key dropped instead
 * of 400ing, and the whitelist would be untestable.
 *
 * NEVER accepted (the RPC ignores them too, but the schema is the first
 * line): `organization_id` (the `:orgId` route param), `created_by`
 * (`auth.uid()` in the RPC), `code` (the `generate_person_code` trigger),
 * `auth_user_id`, every `gdpr_*` column, and `linked_business_id` (only
 * `upsert_person_business` sets it).
 *
 * CHILD ARRAYS are REPLACE-SETS: a present key deletes every stored row of
 * that set and inserts the array (`[]` clears it); an absent key leaves the
 * set alone. Child objects are NOT strict — unknown keys (a legacy caller's
 * `context_type`, a row `id`) are stripped, which is harmless because the RPC
 * reads a fixed key list per child. Discipline context cannot be set through
 * the payload: the RPC always writes `context_type = 'global'`,
 * `context_id = NULL`.
 *
 * EMPTY STRINGS: every optional text field folds `''` (and whitespace) to
 * `null`, so a form that sends a cleared input clears the column rather than
 * storing `''`. Required text (`first_name`, `last_name`, a contact's name)
 * must be non-empty after trimming.
 *
 * Uniqueness the database also enforces is refined here so the user gets a
 * 400 with a readable message instead of a raw 23505: one row per skill, one
 * per discipline (the global unique index), and at most one primary location
 * per `location_type` (`uq_person_locations_primary_per_type`).
 */
import { z } from "zod";
import { calendarDateSchema } from "./dateWindow.js";
import { vocabCodeFromName } from "./dualScopeTypes.js";
// ---------------------------------------------------------------------------
// Vocabularies (moved from apps/web/src/types/people.ts, which re-exports them)
// ---------------------------------------------------------------------------
/**
 * The person-type vocabulary the app offers. `people.person_type` has no
 * CHECK, so the write schemas accept any short token (a legacy value on an
 * existing row must never block an unrelated save); this list is what the
 * pickers, labels and filters use.
 */
export const PERSON_TYPES = [
    "employee",
    "contractor",
    "business_contact",
    "client",
    "volunteer",
    "artist",
    "board_member",
    "advisor",
    "stakeholder",
];
/**
 * The status vocabulary the app offers. `people.status` has no CHECK either,
 * so the write schemas take any short token for the same reason as
 * `PERSON_TYPES`. Archive is `status = 'inactive'`.
 */
export const PERSON_STATUSES = ["active", "inactive", "archived"];
/** `person_locations_location_type_check`. */
export const PERSON_LOCATION_TYPES = ["home", "work", "mailing", "emergency", "other"];
/** `person_skills_proficiency_level_check`. */
export const PERSON_PROFICIENCY_LEVELS = ["beginner", "intermediate", "advanced", "expert"];
// ---------------------------------------------------------------------------
// Field helpers
// ---------------------------------------------------------------------------
/** `''` / whitespace-only → `null`; anything else passes through untouched. */
const blankToNull = (value) => typeof value === "string" && value.trim() === "" ? null : value;
/** Optional, nullable free text; `''` clears the column. */
const optionalText = (max) => z.preprocess(blankToNull, z.string().trim().max(max).nullable()).optional();
/** Optional, nullable email; `''` clears the column. */
const optionalEmail = z
    .preprocess(blankToNull, z.string().trim().email().max(320).nullable())
    .optional();
/** Optional, nullable calendar day (`yyyy-MM-dd`); `''` clears it. */
const optionalDate = z.preprocess(blankToNull, calendarDateSchema.nullable()).optional();
const requiredName = z.string().trim().min(1).max(255);
/** A short vocabulary token (type / status / tag). */
const token = z.string().trim().min(1).max(100);
// ---------------------------------------------------------------------------
// Child arrays (replace-sets)
// ---------------------------------------------------------------------------
export const personDisciplineInputSchema = z.object({
    discipline_id: z.string().uuid(),
    start_date: optionalDate,
    end_date: optionalDate,
    is_primary: z.boolean().optional(),
    notes: optionalText(5000),
});
export const personSkillInputSchema = z.object({
    skill_id: z.string().uuid(),
    proficiency_level: z
        .preprocess(blankToNull, z.enum(PERSON_PROFICIENCY_LEVELS).nullable())
        .optional(),
    years_experience: z.number().int().min(0).max(100).nullable().optional(),
    last_used: optionalDate,
    notes: optionalText(5000),
});
export const personEmergencyContactInputSchema = z.object({
    contact_name: requiredName,
    relationship: optionalText(100),
    phone: optionalText(50),
    email: optionalEmail,
    is_primary: z.boolean().optional(),
});
export const personLocationInputSchema = z.object({
    gis_location_id: z.string().uuid(),
    /** The RPC defaults an absent type to `'home'`. */
    location_type: z.enum(PERSON_LOCATION_TYPES).optional(),
    is_primary: z.boolean().optional(),
    notes: optionalText(5000),
});
const MAX_CHILDREN = 200;
const childArrays = {
    disciplines: z.array(personDisciplineInputSchema).max(MAX_CHILDREN).optional(),
    skills: z.array(personSkillInputSchema).max(MAX_CHILDREN).optional(),
    emergency_contacts: z.array(personEmergencyContactInputSchema).max(MAX_CHILDREN).optional(),
    locations: z.array(personLocationInputSchema).max(MAX_CHILDREN).optional(),
};
// ---------------------------------------------------------------------------
// Scalars
// ---------------------------------------------------------------------------
/** Scalar fields shared by create and update (everything but the two names). */
const personOptionalScalars = {
    middle_name: optionalText(255),
    preferred_name: optionalText(255),
    display_name: optionalText(255),
    email: optionalEmail,
    secondary_email: optionalEmail,
    phone: optionalText(50),
    mobile_phone: optionalText(50),
    work_phone: optionalText(50),
    company: optionalText(255),
    job_title: optionalText(255),
    department: optionalText(255),
    person_type: z.array(token).max(20).nullable().optional(),
    bio: optionalText(10000),
    notes: optionalText(10000),
    tags: z.array(token).max(100).nullable().optional(),
    linkedin_url: optionalText(2048),
    website: optionalText(2048),
    has_system_access: z.boolean().optional(),
    is_business: z.boolean().optional(),
};
/** Photo URLs — accepted by UPDATE only (the upload/remove path writes them). */
const personPhotoFields = {
    profile_photo_small: optionalText(2048),
    profile_photo_medium: optionalText(2048),
    profile_photo_large: optionalText(2048),
    profile_photo_url: optionalText(2048),
};
/** The scalar `p_payload` keys both RPCs read. */
export const PERSON_SCALAR_KEYS = [
    "first_name",
    "middle_name",
    "last_name",
    "preferred_name",
    "display_name",
    "email",
    "secondary_email",
    "phone",
    "mobile_phone",
    "work_phone",
    "company",
    "job_title",
    "department",
    "person_type",
    "status",
    "bio",
    "notes",
    "tags",
    "linkedin_url",
    "website",
    "has_system_access",
    "is_business",
];
/** The replace-set child keys both RPCs read. */
export const PERSON_CHILD_KEYS = ["disciplines", "skills", "emergency_contacts", "locations"];
/** The photo keys only `update_person` reads. */
export const PERSON_PHOTO_KEYS = [
    "profile_photo_small",
    "profile_photo_medium",
    "profile_photo_large",
    "profile_photo_url",
];
function firstDuplicate(values) {
    return new Set(values).size !== values.length;
}
function refineChildArrays(value, ctx) {
    if (value.skills && firstDuplicate(value.skills.map((s) => s.skill_id))) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["skills"],
            message: "Each skill can be added only once",
        });
    }
    if (value.disciplines && firstDuplicate(value.disciplines.map((d) => d.discipline_id))) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["disciplines"],
            message: "Each discipline can be added only once",
        });
    }
    if (value.locations) {
        const primaryTypes = value.locations
            .filter((l) => l.is_primary === true)
            .map((l) => l.location_type ?? "home");
        if (firstDuplicate(primaryTypes)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["locations"],
                message: "Only one location of each type can be primary",
            });
        }
    }
}
// ---------------------------------------------------------------------------
// Create / update
// ---------------------------------------------------------------------------
/**
 * POST /orgs/:orgId/people body. `first_name` / `last_name` are the NOT NULL
 * columns the caller owns; `status` defaults to `'active'` and the two booleans
 * to `false` in the RPC when absent.
 */
export const createPersonSchema = z
    .object({
    first_name: requiredName,
    last_name: requiredName,
    status: token.optional(),
    ...personOptionalScalars,
    ...childArrays,
})
    .strict()
    .superRefine(refineChildArrays);
/**
 * PATCH /orgs/:orgId/people/:personId body — any subset, at least one key.
 * A present scalar key is written (JSON `null` clears it); an absent key is
 * left alone. The names stay non-nullable (NOT NULL columns).
 */
export const updatePersonSchema = z
    .object({
    first_name: requiredName.optional(),
    last_name: requiredName.optional(),
    status: token.optional(),
    ...personOptionalScalars,
    ...personPhotoFields,
    ...childArrays,
})
    .strict()
    .superRefine((value, ctx) => {
    if (Object.keys(value).length === 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "At least one field must be provided" });
    }
    refineChildArrays(value, ctx);
});
// ---------------------------------------------------------------------------
// DEI (PUT /orgs/:orgId/people/:personId/dei — org admins only)
// ---------------------------------------------------------------------------
/** The DEI answer columns — everything but consent and the survey stamp. */
export const PERSON_DEI_ANSWER_KEYS = [
    "gender",
    "pronouns",
    "ethnicity",
    "race",
    "sexual_orientation",
    "disability_status",
    "veteran_status",
    "age_range",
    "primary_language",
    "religious_affiliation",
    "socioeconomic_background",
];
/**
 * The `person_dei` row a PUT REPLACES. Absent answers are written as NULL
 * (PUT is a full replace, not a merge), which is also what the
 * `valid_consent` CHECK needs when consent is withdrawn: with
 * `dei_data_consent = false` every answer must be empty, refined here so the
 * refusal is a readable 400 rather than a raw 23514.
 *
 * NEVER carries `person_id`, `organization_id`, `created_by` or
 * `updated_by` — the route pins them.
 */
export const personDeiSchema = z
    .object({
    gender: optionalText(100),
    pronouns: optionalText(100),
    ethnicity: z.array(token).max(20).nullable().optional(),
    race: z.array(token).max(20).nullable().optional(),
    sexual_orientation: optionalText(100),
    disability_status: optionalText(100),
    veteran_status: optionalText(100),
    age_range: optionalText(100),
    primary_language: optionalText(100),
    religious_affiliation: optionalText(100),
    socioeconomic_background: optionalText(100),
    /** `timestamptz` instant (ISO 8601 with offset). */
    dei_survey_completed_at: z
        .preprocess(blankToNull, z.string().datetime({ offset: true }).nullable())
        .optional(),
    dei_data_consent: z.boolean(),
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.dei_data_consent)
        return;
    for (const key of PERSON_DEI_ANSWER_KEYS) {
        const answer = value[key];
        const empty = answer == null || (Array.isArray(answer) && answer.length === 0);
        if (!empty) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [key],
                message: "DEI answers can only be stored with consent",
            });
        }
    }
});
// ---------------------------------------------------------------------------
// Business identity (POST /orgs/:orgId/people/:personId/business)
// ---------------------------------------------------------------------------
/**
 * The `upsert_person_business` parameters (minus `p_person_id`, the route
 * param). `p_business_type_id` is deliberately NOT accepted: the RPC falls
 * back to the org's default type, and accepting an id would need an org
 * pre-check the FK does not give (the FK ignores RLS).
 */
export const personBusinessSchema = z
    .object({
    name: z.string().trim().min(1).max(255),
    trading_name: optionalText(255),
    country_code: z
        .preprocess(blankToNull, z.string().trim().length(2).toUpperCase().nullable())
        .optional(),
    legal_form_id: z.preprocess(blankToNull, z.string().uuid().nullable()).optional(),
    registration_number: optionalText(100),
    vat_number: optionalText(100),
    iban: optionalText(64),
    bic: optionalText(20),
})
    .strict();
// ---------------------------------------------------------------------------
// Relationship (connection) types
//   POST  /orgs/:orgId/connection-types
//   PATCH /orgs/:orgId/connection-types/:typeId
// ---------------------------------------------------------------------------
/**
 * The `code` a new ORG connection type gets from its name. Kept as an alias
 * of the generalised `vocabCodeFromName` (./dualScopeTypes.ts) — same slug
 * rule as the `20260924112446_connection_types_dual_scope` backfill; always
 * matches `connection_types_code_format` (`^[a-z][a-z0-9_]*$`). Collisions
 * are the route's job (`_2` … `_5`).
 */
export const connectionTypeCodeFromName = vocabCodeFromName;
const connectionTypeName = z.string().trim().min(1).max(100);
/** Directional needs a non-blank reverse name (`connection_types_directional_reverse_name`). */
const REVERSE_NAME_REQUIRED = "A directional relationship needs a reverse name";
/**
 * POST body. Always org-scoped (the `:orgId` param); `code` is derived from
 * `name` by the route and is NEVER accepted, nor are `organization_id`,
 * `sort_order`, `color`, `icon` or `is_active` (a new type is active).
 * `category` is free text (the web offers `CONNECTION_CATEGORIES`).
 */
export const createConnectionTypeSchema = z
    .object({
    name: connectionTypeName,
    reverse_name: optionalText(100),
    is_directional: z.boolean().optional(),
    category: optionalText(100),
    description: optionalText(1000),
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.is_directional === true && value.reverse_name == null) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["reverse_name"], message: REVERSE_NAME_REQUIRED });
    }
});
/**
 * PATCH body — any subset, at least one key; `code` is immutable. The
 * directional refinement applies only when BOTH keys are present; for a
 * partial patch the route merges it with the stored row and 400s when the
 * merged row is directional with a blank reverse name (the DB CHECK is the
 * backstop, 23514 → 400).
 */
export const updateConnectionTypeSchema = z
    .object({
    name: connectionTypeName.optional(),
    reverse_name: optionalText(100),
    is_directional: z.boolean().optional(),
    category: optionalText(100),
    description: optionalText(1000),
    is_active: z.boolean().optional(),
})
    .strict()
    .superRefine((value, ctx) => {
    if (Object.keys(value).length === 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "At least one field must be provided" });
    }
    const bothPresent = "is_directional" in value && "reverse_name" in value;
    if (bothPresent && value.is_directional === true && value.reverse_name == null) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["reverse_name"], message: REVERSE_NAME_REQUIRED });
    }
});
//# sourceMappingURL=people.js.map